import * as React from "react"
import { useEffect, useState } from "react"
import {
	Alert, Box, Chip, CircularProgress, Divider, Drawer, IconButton,
	Paper, Stack, Table, TableBody, TableCell, TableContainer,
	TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material"
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { BACKEND_APIS } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"

import { vtApi as api } from "../api"
import { fmtDateTime, isoMinusDays, todayISO } from "../metricsUtils"


// Default window: last 30 days — the natural "how are my operators doing
// this month" question, matching the AI Costs dashboard default.

// Human duration: seconds → "1h 23m" / "4m 12s" / "45s". Keeps the
// table scannable — nobody reads "5052 seconds".
function fmtDuration(seconds) {
	if (seconds == null) return '—'
	const s = Math.round(Number(seconds))
	if (s < 60) return `${s}s`
	const m = Math.floor(s / 60)
	const rs = s % 60
	if (m < 60) return rs ? `${m}m ${rs}s` : `${m}m`
	const h = Math.floor(m / 60)
	const rm = m % 60
	return rm ? `${h}h ${rm}m` : `${h}h`
}


export default function OperatorMetricsPage() {
	const { showToast } = useAppToast()

	const [startDate, setStartDate] = useState(isoMinusDays(30))
	const [endDate, setEndDate] = useState(todayISO())
	// Outlier cap (minutes): sessions longer than this are excluded from the
	// averages (a 55-min "section" is lunch/idle, not tagging). 0/empty = no cap.
	const [capMin, setCapMin] = useState(20)
	const [loading, setLoading] = useState(false)
	const [rows, setRows] = useState([])

	const [selected, setSelected] = useState(null)   // operator row for drill-down
	const [detail, setDetail] = useState([])
	const [detailLoading, setDetailLoading] = useState(false)

	const load = async () => {
		setLoading(true)
		const params = new URLSearchParams()
		if (startDate) params.set('start_date', startDate)
		if (endDate)   params.set('end_date', endDate)
		if (capMin)    params.set('max_seconds', String(Math.round(Number(capMin) * 60)))
		const r = await api.get(`${BACKEND_APIS.VT}/admin/operator-metrics?${params.toString()}`)
		setLoading(false)
		if (!r.ok) { showToast.error('Failed to load operator metrics: ' + r.error); setRows([]); return }
		setRows(r.data?.items || [])
	}

	useEffect(() => { load() }, [startDate, endDate, capMin])

	const openDetail = async (op) => {
		if (!op.id_user) return
		setSelected(op)
		setDetailLoading(true)
		const params = new URLSearchParams()
		if (startDate) params.set('start_date', startDate)
		if (endDate)   params.set('end_date', endDate)
		if (capMin)    params.set('max_seconds', String(Math.round(Number(capMin) * 60)))
		const r = await api.get(`${BACKEND_APIS.VT}/admin/operator-metrics/${op.id_user}?${params.toString()}`)
		setDetailLoading(false)
		if (!r.ok) { showToast.error('Failed to load detail: ' + r.error); setDetail([]); return }
		setDetail(r.data?.items || [])
	}
	const closeDetail = () => { setSelected(null); setDetail([]) }

	return <PageWrapper
		{...vtPageProps('Operator Metrics', 'Per-operator tagging time — average per section, totals, drill-down')}
	>
		<Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
			<TextField size="small" label="From" type="date" sx={{ width: 180 }}
				value={startDate} onChange={e => setStartDate(e.target.value)}
				InputLabelProps={{ shrink: true }} />
			<TextField size="small" label="To" type="date" sx={{ width: 180 }}
				value={endDate} onChange={e => setEndDate(e.target.value)}
				InputLabelProps={{ shrink: true }} />
			<Tooltip title="Sessions longer than this are treated as outliers (lunch / idle) and excluded from the averages. 0 = no cap.">
				<TextField size="small" label="Ignore sessions >" type="number" sx={{ width: 170 }}
					value={capMin}
					onChange={e => setCapMin(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))}
					InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: 'text.secondary' }}>min</Typography> }} />
			</Tooltip>
			<Box sx={{ flexGrow: 1 }} />
			<Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: 380 }}>
				Time = wall-clock per tagging session. Outliers &gt; {capMin || '∞'}m excluded from averages.
			</Typography>
		</Stack>

		{loading ? (
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
				<CircularProgress size={22} /><Typography variant="body2">Loading…</Typography>
			</Box>
		) : rows.length === 0 ? (
			<Alert severity="info">No tagging activity in this date range.</Alert>
		) : (
			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Operator</TableCell>
							<TableCell align="right">Sections</TableCell>
							<TableCell align="right">Sessions</TableCell>
							<TableCell align="right">Avg / section</TableCell>
							<TableCell align="right">Total time</TableCell>
							<TableCell align="right">Min</TableCell>
							<TableCell align="right">Max</TableCell>
							<TableCell align="right">Outliers</TableCell>
							<TableCell>Last activity</TableCell>
							<TableCell align="right"></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.map(op => (
							<TableRow key={op.id_user ?? 'unassigned'} hover
								sx={{ cursor: op.id_user ? 'pointer' : 'default' }}
								onClick={() => openDetail(op)}>
								<TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{op.email}</Typography></TableCell>
								<TableCell align="right">{op.sections}</TableCell>
								<TableCell align="right">{op.sessions}</TableCell>
								<TableCell align="right">
									<Chip size="small" color="primary" variant="outlined" label={fmtDuration(op.avg_seconds)} />
								</TableCell>
								<TableCell align="right" sx={{ fontWeight: 600 }}>{fmtDuration(op.total_seconds)}</TableCell>
								<TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtDuration(op.min_seconds)}</TableCell>
								<TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtDuration(op.max_seconds)}</TableCell>
								<TableCell align="right">
									{op.outliers > 0
										? <Tooltip title={`${op.outliers} session(s) excluded from the averages`}>
											<Chip size="small" color="warning" variant="outlined" label={op.outliers} />
										</Tooltip>
										: <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>}
								</TableCell>
								<TableCell><Typography variant="caption">{op.last_activity || '—'}</Typography></TableCell>
								<TableCell align="right">
									{op.id_user ? (
										<Tooltip title="Section-by-section detail">
											<IconButton size="small" onClick={(e) => { e.stopPropagation(); openDetail(op) }}>
												<OpenInNewIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									) : null}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>
		)}

		<OperatorDetailDrawer
			operator={selected} rows={detail} loading={detailLoading} onClose={closeDetail}
		/>
	</PageWrapper>
}


function OperatorDetailDrawer({ operator, rows, loading, onClose }) {
	const open = Boolean(operator)
	return <Drawer anchor="right" open={open} onClose={onClose}
		PaperProps={{ sx: { width: { xs: '100%', sm: 640 } } }}>
		<Box sx={{ p: 2 }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
				<Typography variant="h6" noWrap>{operator?.email}</Typography>
				<IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
			</Stack>
			{operator && (
				<Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
					<Chip size="small" label={`${operator.sections} sections`} />
					<Chip size="small" label={`${operator.sessions} sessions`} />
					<Chip size="small" color="primary" variant="outlined" label={`avg ${fmtDuration(operator.avg_seconds)}`} />
					<Chip size="small" variant="outlined" label={`total ${fmtDuration(operator.total_seconds)}`} />
				</Stack>
			)}
			<Divider sx={{ mb: 1.5 }} />
			{loading ? (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
					<CircularProgress size={20} /><Typography variant="body2">Loading detail…</Typography>
				</Box>
			) : rows.length === 0 ? (
				<Alert severity="info">No sessions in this range.</Alert>
			) : (
				<TableContainer component={Paper} variant="outlined">
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Retailer · Section</TableCell>
								<TableCell>Task date</TableCell>
								<TableCell align="right">Duration</TableCell>
								<TableCell>Uploaded</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{rows.map(r => (
								<TableRow key={r.id_run} hover
									sx={r.is_outlier ? { opacity: 0.5 } : undefined}>
									<TableCell>
										<Typography variant="body2">{r.retailer_slug} · {r.section_slug}</Typography>
										{r.kind === 'newsletter' && (
											<Typography variant="caption" sx={{ color: 'info.main' }}>newsletter</Typography>
										)}
									</TableCell>
									<TableCell><Typography variant="caption">{r.task_date || '—'}</Typography></TableCell>
									<TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
										{fmtDuration(r.duration_seconds)}
										{r.is_outlier && (
											<Tooltip title="Outlier — excluded from this operator's averages">
												<Chip size="small" color="warning" variant="outlined" label="outlier" sx={{ ml: 0.5 }} />
											</Tooltip>
										)}
									</TableCell>
									<TableCell><Typography variant="caption">{fmtDateTime(r.uploaded_at)}</Typography></TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</Box>
	</Drawer>
}
